import {
  Money,
  toNumber,
  difference,
  times,
  minus,
  sum,
  createInitialMoney,
  changeCurrency,
} from '@/modules/shared/utils/money'
import { allocateProRata } from '@/modules/shared/utils/allocate-pro-rata'
import { computed } from 'vue'

type Transfer = {
  id: number
  capital: Money
  commitment: {
    id: number
    date: string
    capital: Money
    carried_interest_percentage: number
    investor: {
      name: string
    }
    hurdle: Money
    management_fee_percentage: number
    ownership_without_fees_by_called_amount: number
    preferred_return: Money
    preferred_return_percentage: number
    units: number
  }
  carried_interest_distributed: Money
  interest: Money
  other_fee_distributed: Money
  preferred_return: Money
  preferred_return_limit: Money
  profit: Money
  return_of_capital: Money
  return_of_capital_limit: Money
}
type TransformedTransfer = {
  id: number
  investor_set_commitment_id: number
  is_active: boolean
  name: string
  units: number
  currency: string

  // normal distribution
  capital: Money
  preferred_return: Money
  profit: Money

  // interest distribution
  principal: Money
  interest: Money

  // shared
  total_capital: Money
  carried_interest_distributed: Money
  other_fee_distributed: Money
  net_distributed: Money
  hurdle_remaining: Money

  // hidden fields
  ownership_percentage: number
  commitment: Money
  commitment_date: string
  carried_interest_percentage: number
  original_carried_interest_percentage: number
  preferred_return_percentage: number
  preferred_return_limit: Money
  pre_hurdle: Money
  return_of_capital_limit: Money
}
type Macros = {
  total_capital: Money
  carried_interest_distributed: number
  interest: Money
  other_fee_distributed: Money
}

////////////////////////////////////////
//// Calculations
////////////////////////////////////////

// Calculations for normal distribution
const calculateCapital = (transfer: TransformedTransfer) => {
  const total_capital = transfer.total_capital || createInitialMoney(transfer.currency)
  const return_of_capital_limit = transfer.return_of_capital_limit || createInitialMoney(transfer.currency)
  const capital = toNumber(total_capital) >= toNumber(return_of_capital_limit) ? return_of_capital_limit : total_capital

  return changeCurrency(capital, transfer.currency)
}
const calculatePreferredReturn = (transfer: TransformedTransfer) => {
  let preferred_return = createInitialMoney(transfer.total_capital?.currency)

  const preferred_return_limit = transfer.preferred_return_limit || createInitialMoney(transfer.currency)
  if (toNumber(preferred_return_limit) <= 0) return preferred_return

  const total_capital = transfer.total_capital || createInitialMoney(transfer.currency)
  const capital = transfer.capital || createInitialMoney(transfer.currency)
  const pre_hurdle = transfer.pre_hurdle || createInitialMoney(transfer.currency)
  const capital_remaining = minus(total_capital, capital)
  let hurdle_remaining = minus(pre_hurdle, capital)

  // the commitment hurdle without the preferred_return has been met
  if (toNumber(hurdle_remaining) <= toNumber(preferred_return_limit) && toNumber(capital_remaining) > 0) {
    preferred_return =
      toNumber(capital_remaining) >= toNumber(preferred_return_limit) ? preferred_return_limit : capital_remaining
  }

  if (toNumber(preferred_return) >= toNumber(preferred_return_limit)) preferred_return = preferred_return_limit
  return changeCurrency(preferred_return, transfer.currency)
}
const calculateProfit = (transfer: TransformedTransfer) => {
  let amount = null
  const total_capital = transfer.total_capital || createInitialMoney(transfer.currency)
  const preferred_return = transfer.preferred_return || createInitialMoney(transfer.currency)
  const pre_hurdle = transfer.pre_hurdle || createInitialMoney(transfer.currency)
  let capital = transfer.capital || createInitialMoney(transfer.currency)
  capital = sum([capital, preferred_return])

  if (toNumber(total_capital) < toNumber(capital)) {
    amount = minus(capital, pre_hurdle)
  } else {
    amount = minus(total_capital, capital)
  }
  return toNumber(amount) > 0 ? changeCurrency(amount, transfer.currency) : createInitialMoney(transfer.currency)
}
const calculateCarriedInterest = (transfer: TransformedTransfer, type: string) => {
  const carried_interest_percentage = transfer.carried_interest_percentage || 0
  let profit = transfer.profit || createInitialMoney(transfer.currency)
  if (type === 'interest') {
    profit = transfer.interest || createInitialMoney(transfer.currency)
  }
  profit = times(profit, carried_interest_percentage)

  return changeCurrency(profit, transfer.currency)
}

// Calculations for interest distribution
const calculatePrincipal = (transfer: TransformedTransfer) => {
  const total_capital = transfer.total_capital || createInitialMoney(transfer.currency)
  const interest = transfer.interest || createInitialMoney(transfer.currency)
  const principal = difference([total_capital, interest])

  return changeCurrency(principal, transfer.currency)
}

// Shared calculations
const calculateTotalDistributed = (transfer: TransformedTransfer, type: string) => {
  const other_fee_distributed = transfer.other_fee_distributed || createInitialMoney(transfer.currency)
  const carried_interest_distributed = transfer.carried_interest_distributed || createInitialMoney(transfer.currency)

  // for normal type
  let capital = transfer.capital || createInitialMoney(transfer.currency)
  let profit = transfer.profit || createInitialMoney(transfer.currency)
  let preferred_return = transfer.preferred_return || createInitialMoney(transfer.currency)

  if (type === 'interest') {
    capital = transfer.principal || createInitialMoney(transfer.currency)
    profit = transfer.interest || createInitialMoney(transfer.currency)
    preferred_return = createInitialMoney(transfer.currency)
  }

  // total capital is being calculated because capital and profit can be manually changed
  // the attributes of different types of distribution will not affect each other because it was set to initialMoney in transformTransfer
  const total_capital = sum([capital, preferred_return, profit])
  const value = difference([total_capital, carried_interest_distributed, other_fee_distributed])

  return toNumber(value) > 0 ? changeCurrency(value, transfer.currency) : createInitialMoney(transfer.currency)
}
const calculateHurdleRemaining = (transfer: TransformedTransfer, type: string) => {
  const pre_hurdle = transfer.pre_hurdle || createInitialMoney(transfer.currency)
  let capital = transfer.capital || createInitialMoney(transfer.currency)
  let preferred_return = transfer.preferred_return || createInitialMoney(transfer.currency)

  if (type === 'interest') {
    capital = transfer.principal || createInitialMoney(transfer.currency)
    preferred_return = createInitialMoney(transfer.currency)
  }

  capital = sum([capital, preferred_return])
  const hurdle_remaining = minus(pre_hurdle, capital)
  if (toNumber(hurdle_remaining) <= 0) return createInitialMoney(transfer.currency)
  return changeCurrency(hurdle_remaining, transfer.currency)
}
const calculateTransferValues = (
  transfer: TransformedTransfer,
  type: string,
  config,
  macros?: Macros,
  input_key?: string,
) => {
  if (!macros) {
    // this is for the transfers fields
    // we use switch to prevent the calculations of the specific column to affect its previous columns
    switch (input_key) {
      case 'total_capital':
        transfer.capital = calculateCapital(transfer)
        transfer.preferred_return = calculatePreferredReturn(transfer)
        transfer.profit = calculateProfit(transfer)
        transfer.principal = calculatePrincipal(transfer)
        break
    }

    if (!['carried_interest_distributed', 'other_fee_distributed'].includes(input_key))
      transfer.carried_interest_distributed = calculateCarriedInterest(transfer, type)
  } else {
    // this is for the auto calculations
    transfer.capital = calculateCapital(transfer)
    transfer.preferred_return = calculatePreferredReturn(transfer)
    transfer.profit = calculateProfit(transfer)
    transfer.principal = calculatePrincipal(transfer)
    transfer.carried_interest_distributed = calculateCarriedInterest(transfer, type)
  }

  transfer.net_distributed = calculateTotalDistributed(transfer, type)
  transfer.hurdle_remaining = calculateHurdleRemaining(transfer, type)
  return transfer
}

export const getDisbursementType = (
  type: 'Equity' | 'Convertible Note' | 'Loan' | 'Revenue Share' | 'Option' | 'Warrant' | 'Safe Note',
) => {
  if (['Loan', 'Safe Note', 'Convertible Note'].includes(type)) return 'interest'
  // if (['Equity', 'Revenue Share', 'Option', 'Warrant'].includes(type)) return 'portfolio'
  return 'normal'
}
export const useEuropeanDisbursement = (
  type: 'Equity' | 'Convertible Note' | 'Loan' | 'Revenue Share' | 'Option' | 'Warrant' | 'Safe Note',
  configComputed,
) => {
  const config = computed(() => configComputed.value)
  const disbursement_type = getDisbursementType(type)

  const transformTransfer = (transfer: Transfer) => {
    let t: TransformedTransfer = {
      id: transfer.id,
      investor_set_commitment_id: transfer.commitment.id,
      is_active: true,
      name: transfer.commitment.investor.name,
      units: transfer.commitment.units,
      currency: config.value.currency,

      // normal distribution
      capital: transfer.return_of_capital,
      preferred_return: transfer.preferred_return,
      profit: transfer.profit,

      // interest distribution
      principal: transfer.return_of_capital,
      interest: transfer.interest,

      // shared
      carried_interest_distributed: transfer.carried_interest_distributed,
      total_capital: transfer.capital,
      other_fee_distributed: transfer.other_fee_distributed,
      net_distributed: createInitialMoney(config.value.currency),
      hurdle_remaining: createInitialMoney(config.value.currency),

      // fields needed for computation
      return_of_capital_limit: transfer.return_of_capital_limit,
      preferred_return_limit: transfer.preferred_return_limit,
      pre_hurdle: transfer.commitment.hurdle,
      ownership_percentage: transfer.commitment.ownership_without_fees_by_called_amount,
      commitment: transfer.commitment.capital,
      commitment_date: transfer.commitment.date,
      carried_interest_percentage: transfer.commitment.carried_interest_percentage || 0,
      original_carried_interest_percentage: transfer.commitment.carried_interest_percentage || 0,
      preferred_return_percentage: transfer.commitment.preferred_return_percentage,
    }

    t.hurdle_remaining = calculateHurdleRemaining(t, disbursement_type)
    t.net_distributed = calculateTotalDistributed(t, disbursement_type)

    return t
  }
  // prettier-ignore
  const updateTransfer = (transfer: TransformedTransfer, key?: string) => calculateTransferValues(transfer, disbursement_type, config.value, null, key)
  const macrosUpdateTransfer = (transfer: TransformedTransfer, macros: Macros, key: string) => {
    if (key !== 'carried_interest_distributed') {
      const amount = macros[key] || createInitialMoney(config.value.currency)
      const output = times(amount, transfer.ownership_percentage)
      transfer[key] = toNumber(output) > 0 ? output : createInitialMoney(output.currency)
    } else {
      let percent = (macros.carried_interest_distributed || 0) / 100
      if (!macros.carried_interest_distributed && macros.carried_interest_distributed !== 0)
        percent = transfer.original_carried_interest_percentage
      transfer.carried_interest_percentage = percent
    }
    transfer = calculateTransferValues(transfer, disbursement_type, config.value, macros)
    return transfer
  }
  const recalculateTransfer = (transfer: TransformedTransfer) => {
    transfer.capital = calculateCapital(transfer)
    transfer.preferred_return = calculatePreferredReturn(transfer)
    transfer.profit = calculateProfit(transfer)
    transfer.principal = calculatePrincipal(transfer)
    transfer.carried_interest_distributed = calculateCarriedInterest(transfer, disbursement_type)
    transfer.net_distributed = calculateTotalDistributed(transfer, disbursement_type)
    transfer.hurdle_remaining = calculateHurdleRemaining(transfer, disbursement_type)
    return transfer
  }

  const batchAllocateProRata = (transfers: TransformedTransfer[], macros: Macros, key: string) => {
    const active = transfers.filter((t) => t.is_active)
    if (active.length === 0) return

    // Apply pro-rata LRM for dollar-amount keys (not carried_interest which is percentage-based)
    if (key !== 'carried_interest_distributed' && macros[key]) {
      const total = macros[key] as Money
      const weights = active.map((t) => t.ownership_percentage || 0)
      const allocations = allocateProRata(total, weights)
      active.forEach((t, i) => {
        t[key] = allocations[i]
        recalculateTransfer(t)
      })
    }

    // Round per-investor derived fields to 2 decimal places
    roundDerivedFields(active)
  }

  const roundMoney = (money: Money): Money => ({
    ...money,
    amount: parseFloat(money.amount || '0').toFixed(2),
    common_amount: parseFloat(money.common_amount || '0').toFixed(2),
  })

  const roundDerivedFields = (active: TransformedTransfer[]) => {
    if (active.length === 0) return
    active.forEach((t) => {
      t.preferred_return = roundMoney(t.preferred_return)
      t.profit = calculateProfit(t)
      t.carried_interest_distributed = roundMoney(calculateCarriedInterest(t, disbursement_type))
      t.net_distributed = calculateTotalDistributed(t, disbursement_type)
      t.hurdle_remaining = calculateHurdleRemaining(t, disbursement_type)
    })
  }

  const calculateAggregate = (transfers: TransformedTransfer[]) => {
    const normal_distribution_keys = ['capital', 'profit', 'preferred_return']
    const interest_distribution_keys = ['principal', 'interest']
    const shared_keys = [
      'total_capital',
      'other_fee_distributed',
      'net_distributed',
      'hurdle_remaining',
      'carried_interest_distributed',
    ]
    let keys = []

    if (disbursement_type === 'normal') {
      keys = normal_distribution_keys.concat(shared_keys)
    } else {
      keys = interest_distribution_keys.concat(shared_keys)
    }

    return transfers.reduce((acc, transfer) => {
      if (transfer.is_active) {
        keys.forEach((key) => {
          const acc_value = acc[key] || createInitialMoney(transfer[key]?.currency)
          const transfer_value = transfer[key] || createInitialMoney(transfer[key]?.currency)
          acc[key] = sum([acc_value, transfer_value])
        })
      }
      return acc
    }, {})
  }
  const updateCurrency = (transfers: TransformedTransfer[], macros: Macros, currency: string) => {
    transfers.forEach((transfer) => {
      transfer.currency = currency
      transfer.capital = changeCurrency(transfer.capital, currency)
      transfer.preferred_return = changeCurrency(transfer.preferred_return, currency)
      transfer.profit = changeCurrency(transfer.profit, currency)
      transfer.principal = changeCurrency(transfer.principal, currency)
      transfer.interest = changeCurrency(transfer.interest, currency)
      transfer.total_capital = changeCurrency(transfer.total_capital, currency)
      transfer.carried_interest_distributed = changeCurrency(transfer.carried_interest_distributed, currency)
      transfer.other_fee_distributed = changeCurrency(transfer.other_fee_distributed, currency)
      transfer.net_distributed = changeCurrency(transfer.net_distributed, currency)
      transfer.hurdle_remaining = changeCurrency(transfer.hurdle_remaining, currency)
    })

    macros.total_capital = changeCurrency(macros.total_capital, currency)
    macros.interest = changeCurrency(macros.interest, currency)
    macros.other_fee_distributed = changeCurrency(macros.other_fee_distributed, currency)
  }

  return {
    transformTransfer,
    updateTransfer: updateTransfer,
    macrosUpdateTransfer,
    recalculateTransfer,
    batchAllocateProRata,
    calculateAggregate,
    updateCurrency,
  }
}
